<template>
  <Section id="platform" tone="default" pad-y="lg" aria-labelledby="platform-showcase-heading">
    <Container>
      <div class="mb-10 flex flex-col gap-3">
        <h2 id="platform-showcase-heading" class="text-3xl font-bold text-ink-strong md:text-4xl">
          {{ t('sections.platform.showcase.title') }}
        </h2>
        <p class="max-w-2xl text-base text-ink-muted md:text-lg">
          {{ t('sections.platform.showcase.subtitle') }}
        </p>
        <p class="max-w-3xl text-sm text-ink md:text-base">
          {{ t('sections.platform.showcase.intro') }}
        </p>
      </div>

      <div
        role="region"
        :aria-label="t('sections.platform.showcase.title')"
        class="flex flex-col gap-6"
        @keydown="onKeydown"
      >
        <!-- Group tabs -->
        <div
          role="tablist"
          :aria-label="t('sections.platform.showcase.title')"
          class="-mx-4 flex items-center gap-1 overflow-x-auto px-4 pb-1 md:mx-0 md:flex-wrap md:overflow-visible md:px-0"
        >
          <button
            v-for="tab in GROUPS"
            :id="`platform-showcase-tab-${tab.id}`"
            :key="tab.id"
            type="button"
            role="tab"
            :aria-selected="group === tab.id"
            :aria-controls="'platform-showcase-panel'"
            :tabindex="group === tab.id ? 0 : -1"
            :class="[
              'inline-flex shrink-0 items-center rounded-full px-3 py-1.5 text-sm font-semibold transition-colors',
              'focus-visible:outline-none focus-visible:shadow-[var(--shadow-ring-focus)]',
              group === tab.id
                ? 'bg-accent-soft text-accent-strong'
                : 'bg-paper-muted text-ink hover:bg-paper',
            ]"
            @click="selectGroup(tab.id)"
          >
            {{ t(tab.labelKey) }}
          </button>
        </div>

        <!-- Desktop two-column / Mobile single-column panel -->
        <div id="platform-showcase-panel" class="grid grid-cols-1 gap-6 lg:grid-cols-[2fr_1fr]">
          <!-- Main image + caption -->
          <div class="flex flex-col gap-4">
            <div class="relative rounded-2xl border border-stroke bg-paper shadow-sm">
              <!-- letterboxed container keeps layout stable across aspect ratios -->
              <div class="relative aspect-[16/10] w-full overflow-hidden rounded-2xl">
                <img
                  v-if="activeItem.cropTop"
                  :key="activeItem.id"
                  :src="activeItem.src"
                  :alt="activeTitle"
                  width="1440"
                  height="900"
                  loading="lazy"
                  decoding="async"
                  class="absolute inset-0 h-full w-full object-cover object-[50%_20%] transition-opacity duration-[var(--duration-base)] motion-reduce:transition-none"
                />
                <img
                  v-else
                  :key="activeItem.id"
                  :src="activeItem.src"
                  :alt="activeTitle"
                  width="1440"
                  height="900"
                  loading="lazy"
                  decoding="async"
                  class="absolute inset-0 h-full w-full object-contain object-center transition-opacity duration-[var(--duration-base)] motion-reduce:transition-none"
                />
              </div>

              <!-- Prev/Next overlay controls -->
              <div
                class="pointer-events-none absolute inset-y-0 left-0 right-0 flex items-center justify-between px-2"
              >
                <BaseIconButton
                  variant="ghost"
                  :label="t('sections.platform.showcase.prev')"
                  class="pointer-events-auto bg-paper/85 shadow-sm hover:bg-paper"
                  @click="goPrev"
                >
                  <BaseIcon name="chevron-left" :size="20" />
                </BaseIconButton>
                <BaseIconButton
                  variant="ghost"
                  :label="t('sections.platform.showcase.next')"
                  class="pointer-events-auto bg-paper/85 shadow-sm hover:bg-paper"
                  @click="goNext"
                >
                  <BaseIcon name="chevron-right" :size="20" />
                </BaseIconButton>
              </div>
            </div>

            <!-- Caption — aria-live so screen readers announce changes -->
            <div
              aria-live="polite"
              aria-atomic="true"
              class="flex flex-col gap-2"
              data-testid="platform-showcase-caption"
            >
              <h3 class="text-lg font-bold text-ink-strong">
                {{ activeTitle }}
              </h3>
              <p class="text-sm text-ink">
                {{ activeDescription }}
              </p>
              <p class="border-l-2 border-stroke pl-3 text-sm italic text-ink-muted">
                {{ activeComment }}
              </p>
              <p class="text-xs text-ink-muted">
                <span class="font-semibold"> {{ t('sections.platform.showcase.source') }}: </span>
                <a
                  :href="activeItem.source"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="underline underline-offset-2 hover:text-accent-strong"
                >
                  promo-kit.ru
                </a>
              </p>
            </div>
          </div>

          <!-- Thumbnail rail -->
          <div class="flex flex-col gap-3">
            <!-- Desktop: vertical rail. Ограничиваем высотой, чтобы миниатюры не
                 выходили за блок при табе «Все» (16 элементов). Scroll-snap
                 намеренно не используем — на колесе мыши mandatory-snap даёт
                 рваный/«подтупливающий» скролл. -->
            <ul class="hidden max-h-[560px] flex-col gap-2 overflow-y-auto pr-1 lg:flex">
              <li v-for="item in filtered" :key="item.id">
                <button
                  type="button"
                  :aria-label="localizedTitle(item)"
                  :aria-current="item.id === activeId ? 'true' : undefined"
                  :class="[
                    'group flex w-full items-center gap-3 rounded-xl border p-2 text-left transition-colors',
                    'focus-visible:outline-none focus-visible:shadow-[var(--shadow-ring-focus)]',
                    item.id === activeId
                      ? 'border-transparent bg-accent-soft ring-2 ring-inset ring-accent-strong'
                      : 'border-stroke bg-paper hover:bg-paper-muted',
                  ]"
                  @click="selectById(item.id)"
                >
                  <span
                    class="relative block h-[60px] w-[96px] shrink-0 overflow-hidden rounded-md border border-stroke bg-paper-muted"
                  >
                    <img
                      :src="item.src"
                      :alt="''"
                      aria-hidden="true"
                      width="96"
                      height="60"
                      loading="lazy"
                      decoding="async"
                      class="absolute inset-0 h-full w-full object-cover object-top"
                    />
                  </span>
                  <span
                    :class="[
                      'min-w-0 flex-1 truncate text-sm font-medium',
                      item.id === activeId ? 'text-accent-strong' : 'text-ink',
                    ]"
                  >
                    {{ localizedTitle(item) }}
                  </span>
                </button>
              </li>
            </ul>

            <!-- Mobile: horizontal rail -->
            <ul
              class="-mx-4 flex gap-3 overflow-x-auto px-4 pb-2 lg:hidden"
              style="scroll-snap-type: x mandatory"
            >
              <li
                v-for="item in filtered"
                :key="item.id"
                class="shrink-0"
                style="scroll-snap-align: start"
              >
                <button
                  type="button"
                  :aria-label="localizedTitle(item)"
                  :aria-current="item.id === activeId ? 'true' : undefined"
                  :class="[
                    'flex w-[140px] flex-col gap-2 rounded-xl border p-2 text-left transition-colors',
                    'focus-visible:outline-none focus-visible:shadow-[var(--shadow-ring-focus)]',
                    item.id === activeId
                      ? 'border-transparent bg-accent-soft ring-2 ring-inset ring-accent-strong'
                      : 'border-stroke bg-paper hover:bg-paper-muted',
                  ]"
                  @click="selectById(item.id)"
                >
                  <span
                    class="relative block aspect-[16/10] w-full overflow-hidden rounded-md border border-stroke bg-paper-muted"
                  >
                    <img
                      :src="item.src"
                      :alt="''"
                      aria-hidden="true"
                      width="140"
                      height="88"
                      loading="lazy"
                      decoding="async"
                      class="absolute inset-0 h-full w-full object-cover object-top"
                    />
                  </span>
                  <span
                    :class="[
                      'truncate text-xs font-medium',
                      item.id === activeId ? 'text-accent-strong' : 'text-ink',
                    ]"
                  >
                    {{ localizedTitle(item) }}
                  </span>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </Container>
  </Section>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useHead } from '@unhead/vue';
import { useI18n } from 'vue-i18n';
import Section from '@/components/primitives/Section.vue';
import Container from '@/components/primitives/Container.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import BaseIconButton from '@/components/base/BaseIconButton.vue';
import { PLATFORM_SCREENSHOTS, type PlatformScreenshot } from '@/content/platform-screenshots';
import { useLocale } from '@/composables/useLocale';
import { pickLocalized } from '@/lib/i18n/localize';

type Group = 'all' | 'builder' | 'crm' | 'operations' | 'other';

interface GroupTab {
  readonly id: Group;
  readonly labelKey: string;
}

const GROUPS: readonly GroupTab[] = [
  { id: 'all', labelKey: 'sections.platform.showcase.groupAll' },
  { id: 'builder', labelKey: 'sections.platform.showcase.groupBuilder' },
  { id: 'crm', labelKey: 'sections.platform.showcase.groupCrm' },
  { id: 'operations', labelKey: 'sections.platform.showcase.groupOperations' },
  { id: 'other', labelKey: 'sections.platform.showcase.groupOther' },
];

const { t } = useI18n();
const { locale } = useLocale();

const group = ref<Group>('all');
const activeId = ref<string>(PLATFORM_SCREENSHOTS[0].id);

const filtered = computed<readonly PlatformScreenshot[]>(() =>
  group.value === 'all'
    ? PLATFORM_SCREENSHOTS
    : PLATFORM_SCREENSHOTS.filter((s) => s.group === group.value),
);

const activeItem = computed<PlatformScreenshot>(() => {
  const match = PLATFORM_SCREENSHOTS.find((s) => s.id === activeId.value);
  return match ?? PLATFORM_SCREENSHOTS[0];
});

const activeIndex = computed<number>(() =>
  filtered.value.findIndex((s) => s.id === activeId.value),
);

const activeTitle = computed<string>(() => pickLocalized(activeItem.value, 'title', locale.value));
const activeDescription = computed<string>(() =>
  pickLocalized(activeItem.value, 'description', locale.value),
);
const activeComment = computed<string>(() =>
  pickLocalized(activeItem.value, 'comment', locale.value),
);

watch(filtered, (list) => {
  if (list.length === 0) return;
  if (!list.some((s) => s.id === activeId.value)) {
    activeId.value = list[0].id;
  }
});

function localizedTitle(item: PlatformScreenshot): string {
  return pickLocalized(item, 'title', locale.value);
}

function selectGroup(next: Group): void {
  group.value = next;
}

function selectById(id: string): void {
  activeId.value = id;
}

function goPrev(): void {
  const list = filtered.value;
  if (list.length === 0) return;
  const idx = activeIndex.value;
  const nextIdx = idx <= 0 ? list.length - 1 : idx - 1;
  activeId.value = list[nextIdx].id;
}

function goNext(): void {
  const list = filtered.value;
  if (list.length === 0) return;
  const idx = activeIndex.value;
  const nextIdx = idx >= list.length - 1 ? 0 : idx + 1;
  activeId.value = list[nextIdx].id;
}

function onKeydown(event: KeyboardEvent): void {
  const list = filtered.value;
  if (list.length === 0) return;
  switch (event.key) {
    case 'ArrowLeft':
      event.preventDefault();
      goPrev();
      break;
    case 'ArrowRight':
      event.preventDefault();
      goNext();
      break;
    case 'Home':
      event.preventDefault();
      activeId.value = list[0].id;
      break;
    case 'End':
      event.preventDefault();
      activeId.value = list[list.length - 1].id;
      break;
    default:
      break;
  }
}

// Preload LCP image (first screenshot) to avoid layout jank.
useHead({
  link: [
    {
      rel: 'preload',
      as: 'image',
      href: PLATFORM_SCREENSHOTS[0].src,
    },
  ],
});
</script>
